import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import HttpClient from '@/api/HttpClient';
import { useNavigate } from 'react-router-dom';

const httpClient = new HttpClient();

interface LoginPayload {
  email: string;
  password: string;
}

export interface BusinessUnitDto {
  id: number;
  code: string;
  name: string;
  /** Presigned S3 URL; short-lived. Absent or null if no logo uploaded. */
  logoUrl?: string | null;
  /** Square icon (sidebar collapsed, cards). */
  iconUrl?: string | null;
  /** Curated UI font id; default from server is `space-grotesk`. */
  themeFontKey?: string;
  /** IANA zone for count / visit calendar days. */
  countCalendarTimeZone?: string;
  /** Server-computed today in `countCalendarTimeZone` (`YYYY-MM-DD`). */
  countCalendarTodayYmd?: string;
}

export interface LoginResponse {
  token: string;
  user: any;
  businessUnits?: BusinessUnitDto[];
  currentBusinessUnit?: BusinessUnitDto;
  success?: boolean;
}

interface GoogleLoginPayload {
  token: string;
}

interface RegisterPayload {
  email: string;
  password: string;
  [key: string]: any;
}

const loginApi = async (payload: LoginPayload): Promise<LoginResponse> => {
  const response = await httpClient.post<LoginResponse>('api/auth/login', payload);
  return response.data;
};

const googleLoginApi = async (payload: GoogleLoginPayload): Promise<LoginResponse> => {
  const response = await httpClient.post<LoginResponse>('api/auth/google-login', payload);
  return response.data;
};

const registerApi = async (payload: RegisterPayload): Promise<LoginResponse> => {
  const response = await httpClient.post<LoginResponse>('api/auth/register', payload);
  return response.data;
};

const refreshAuthApi = async (): Promise<LoginResponse> => {
  const response = await httpClient.get<LoginResponse>('api/auth/refresh-auth', {});
  return response.data;
};

const setCurrentBusinessUnitApi = async (businessUnitId: number): Promise<LoginResponse & { success?: boolean }> => {
  const response = await httpClient.post<LoginResponse & { success?: boolean }>(
    'api/user/set-current-business-unit',
    { businessUnitId },
  );
  return response.data as LoginResponse & { success?: boolean };
};

export function enrichUserFromLogin(data: LoginResponse) {
  return {
    ...data.user,
    businessUnits: data.businessUnits,
    currentBusinessUnit: data.currentBusinessUnit,
  };
}

export const useLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: loginApi,
    onSuccess: (data) => {
      window.localStorage.setItem('jwt', data.token);
      queryClient.setQueryData(['currentUser'], enrichUserFromLogin(data));
      const n = data.businessUnits?.length ?? 0;
      if (n <= 1) {
        navigate('/app/dashboard');
      }
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });
};

export const useGoogleLogin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: googleLoginApi,
    onSuccess: (data) => {
      window.localStorage.setItem('jwt', data.token);
      queryClient.setQueryData(['currentUser'], enrichUserFromLogin(data));
      const n = data.businessUnits?.length ?? 0;
      if (n <= 1) {
        navigate('/app/dashboard');
      }
    },
    onError: (error) => {
      console.error('Google login failed:', error);
    },
  });
};

export const useRegister = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: registerApi,
    onSuccess: (data) => {
      window.localStorage.setItem('jwt', data.token);
      queryClient.setQueryData(['currentUser'], enrichUserFromLogin(data));
      const n = data.businessUnits?.length ?? 0;
      if (n <= 1) {
        navigate('/app/dashboard');
      }
    },
    onError: (error) => {
      console.error('Registration failed:', error);
    },
  });
};

/**
 * Manual refresh: `refetch()`. v5 removed `onSuccess` on `useQuery`, so we sync
 * `currentUser` from `data` in an effect when the refresh result lands.
 */
export const useRefreshAuth = () => {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['refreshAuth'],
    queryFn: refreshAuthApi,
    enabled: false, // Only run when manually triggered
    retry: false,
  });

  useEffect(() => {
    const data = query.data;
    if (data && data.token) {
      window.localStorage.setItem('jwt', data.token);
      queryClient.setQueryData(['currentUser'], enrichUserFromLogin(data));
    }
  }, [query.data, queryClient]);

  return query;
};

export const useSetCurrentBusinessUnit = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setCurrentBusinessUnitApi,
    onSuccess: (data) => {
      if (data?.token) {
        window.localStorage.setItem('jwt', data.token);
        queryClient.setQueryData(['currentUser'], enrichUserFromLogin(data));
      }
    },
  });
};

export const useLogout = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      // Clear localStorage
      window.localStorage.clear();
      // Clear React Query cache
      queryClient.clear();
      // Navigate to login
      navigate('/app');
    },
  });
};

export const useCurrentUser = () => {
  const queryClient = useQueryClient();
  
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const jwt = window.localStorage.getItem('jwt');
      if (!jwt) return null;
      
      // Try to get from cache first
      const cachedUser = queryClient.getQueryData(['currentUser']);
      if (cachedUser) return cachedUser;
      
      // If not in cache, refresh auth
      const response = await refreshAuthApi();
      return response.user;
    },
    enabled: !!window.localStorage.getItem('jwt'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
